import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIsWide } from '../lib/media.js';

import {
  ApiError,
  clearRequests,
  extendEndpoint,
  listRequests,
  type CapturedRequest,
  type EndpointInfo,
} from '../lib/api.js';
import { exampleCurl } from '../lib/curl.js';
import { expiresSoon, hasExpired, timeUntil } from '../lib/format.js';
import { navigate } from '../lib/router.js';
import { findSaved, refreshSaved, saveToken } from '../lib/storage.js';
import { RulesEditor } from '../rules/RulesEditor.jsx';
import { openRequestStream, type StreamStatus } from '../lib/stream.js';
import { ConnectionBadge } from '../components/ConnectionBadge.js';
import { CopyButton } from '../components/CopyButton.js';
import { RequestDetail } from '../components/RequestDetail.js';
import { RequestList } from '../components/RequestList.js';

/**
 * Mirrors the server's ring buffer. Without it a tab left open for a day grows
 * without bound, holding bodies the server threw away hours ago.
 */
const MAX_KEPT = 500;

/** Coarse enough that a long list is not re-rendered 60 times a minute. */
const CLOCK_TICK_MS = 2_000;

type Pane = 'requests' | 'rules';

const PANES: { id: Pane; label: string }[] = [
  { id: 'requests', label: 'Requests' },
  { id: 'rules', label: 'Reglas' },
];

export interface InspectorProps {
  slug: string;
}

export function Inspector({ slug }: InspectorProps) {
  const [token, setToken] = useState(() => findSaved(slug)?.token);
  const [endpoint, setEndpoint] = useState<EndpointInfo>();
  const [requests, setRequests] = useState<CapturedRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [status, setStatus] = useState<StreamStatus>({ kind: 'connecting' });
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [method, setMethod] = useState('');
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [pane, setPane] = useState<Pane>('requests');
  const [renewing, setRenewing] = useState(false);

  // Below `lg` there is not room for list and detail at once, so the two become
  // one column you move between — rather than a tall page where picking a row
  // scrolls you somewhere and going back means scrolling to find the list again.
  const wide = useIsWide();

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  /** Newest first, no duplicates, capped like the server's ring buffer. */
  const merge = useCallback((incoming: CapturedRequest[], where: 'newer' | 'older') => {
    setRequests((current) => {
      const seen = new Set(current.map((request) => request.id));
      const fresh = incoming.filter((request) => !seen.has(request.id));
      if (fresh.length === 0) return current;

      const merged = where === 'newer' ? [...fresh, ...current] : [...current, ...fresh];
      merged.sort((a, b) => b.seq - a.seq);
      return merged.slice(0, MAX_KEPT);
    });
  }, []);

  // First page. Runs once per slug/token; the stream takes over from there.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    listRequests(slug, token)
      .then((page) => {
        if (cancelled) return;
        setEndpoint(page.endpoint);
        refreshSaved(page.endpoint);
        setRequests(page.requests);
        setCursor(page.nextCursor);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.isAuthFailure) {
          setToken(undefined);
          setError('El token guardado no vale para este endpoint.');
          return;
        }
        setError(
          caught instanceof ApiError && caught.isGone
            ? 'Este endpoint ya no existe o ha caducado.'
            : 'No se pudieron cargar los requests.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  // Keeps the stream's resume point current without making `requests` a
  // dependency of the effect below.
  const requestsRef = useRef(requests);
  requestsRef.current = requests;

  // The live feed. Opened once the first page is in, resuming from its newest
  // `seq` so nothing captured during the load falls between the two.
  useEffect(() => {
    if (!token || loading || error) return;

    const highestSeq = requestsRef.current[0]?.seq ?? 0;

    const stop = openRequestStream({
      slug,
      token,
      lastSeq: highestSeq,
      onRequest: (request) => merge([request], 'newer'),
      onCleared: () => {
        setRequests([]);
        setSelectedId(undefined);
      },
      onReady: (ready) => {
        setEndpoint(ready.endpoint);
        refreshSaved(ready.endpoint);
      },
      onStatus: setStatus,
    });

    return stop;
    // `requests` is deliberately absent: the stream must not be torn down and
    // rebuilt on every arrival. The resume point is read through the ref above.
  }, [slug, token, loading, error, merge]);

  const onLoadMore = useCallback(async () => {
    if (!token || !cursor) return;

    try {
      const page = await listRequests(slug, token, { cursor });
      merge(page.requests, 'older');
      setCursor(page.nextCursor);
    } catch {
      setError('No se pudieron cargar más requests.');
    }
  }, [slug, token, cursor, merge]);

  const onClear = useCallback(async () => {
    if (!token) return;
    if (!window.confirm('¿Vaciar el historial de este endpoint? No se puede deshacer.')) return;

    try {
      await clearRequests(slug, token);
      // The server also broadcasts `cleared`, but this tab should not have to
      // wait for its own round trip to look right.
      setRequests([]);
      setSelectedId(undefined);
      setCursor(null);
    } catch {
      setError('No se pudo vaciar el historial.');
    }
  }, [slug, token]);

  // One click to push the expiry out. Works while the record still exists,
  // so a just-expired endpoint can still be rescued before the cleanup deletes it.
  const onRenew = useCallback(async () => {
    if (!token) return;
    setRenewing(true);
    try {
      const updated = await extendEndpoint(slug, token);
      setEndpoint(updated);
      refreshSaved(updated);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.isGone
          ? 'El endpoint ya no existe: la limpieza lo borró.'
          : 'No se pudo renovar la caducidad.',
      );
    } finally {
      setRenewing(false);
    }
  }, [slug, token]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return requests.filter((request) => {
      if (method && request.method !== method) return false;
      if (needle && !request.path.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [requests, method, search]);

  const methods = useMemo(
    () => [...new Set(requests.map((request) => request.method))].sort(),
    [requests],
  );

  const selected = filtered.find((request) => request.id === selectedId);

  const onSelect = useCallback((request: CapturedRequest) => {
    setSelectedId(request.id);
  }, []);

  if (!token) {
    return <TokenPrompt slug={slug} error={error} onToken={setToken} />;
  }

  const captureUrl = endpoint?.url ?? `${window.location.origin}/e/${slug}`;

  return (
    // `h-[100dvh]` + `overflow-hidden`: the shell is exactly the viewport and
    // never scrolls itself. Only the panes inside do, so the header and the
    // filters stay put and there is never a second scrollbar racing the first.
    // `dvh` rather than `vh` because mobile browser chrome changes the latter.
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      <header className="shrink-0 border-b border-slate-800 px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          >
            ← Inicio
          </button>

          <code className="min-w-0 flex-1 break-all font-mono text-sm text-slate-100">
            {captureUrl}
          </code>

          <CopyButton value={captureUrl} label="Copiar URL" />
          <ConnectionBadge status={status} />
        </div>

        {endpoint && (
          <ExpiryNotice
            expiresAt={endpoint.expiresAt}
            now={now}
            renewing={renewing}
            onRenew={() => void onRenew()}
          />
        )}

        <div role="tablist" aria-label="Secciones del endpoint" className="mt-3 flex gap-1">
          {PANES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={pane === id}
              tabIndex={pane === id ? 0 : -1}
              onClick={() => setPane(id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${
                pane === id
                  ? 'bg-slate-800 text-slate-100'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {pane === 'rules' ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          {/* The endpoint this inspector is already watching. No slug to retype,
              no token to paste again: the session is the one you arrived with. */}
          <RulesEditor credentials={{ slug, token }} />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,26rem)_1fr]">
          {(wide || !selected) && (
            <section className="flex min-h-0 flex-col border-slate-800 lg:border-r">
              <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-800 p-3">
                <label className="sr-only" htmlFor="method-filter">
                  Filtrar por método
                </label>
                <select
                  id="method-filter"
                  value={method}
                  onChange={(event) => setMethod(event.target.value)}
                  className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                >
                  <option value="">Todos los métodos</option>
                  {methods.map((verb) => (
                    <option key={verb} value={verb}>
                      {verb}
                    </option>
                  ))}
                </select>

                <label className="sr-only" htmlFor="path-search">
                  Buscar por path
                </label>
                <input
                  id="path-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por path…"
                  className="min-w-0 flex-1 rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                />

                <button
                  type="button"
                  onClick={() => void onClear()}
                  disabled={requests.length === 0}
                  className="rounded-md border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 transition hover:border-rose-500/50 hover:text-rose-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-40"
                >
                  Vaciar
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {loading ? (
                  <p className="p-4 text-sm text-slate-500">Cargando…</p>
                ) : requests.length === 0 ? (
                  <EmptyState captureUrl={captureUrl} />
                ) : filtered.length === 0 ? (
                  <p className="p-4 text-sm text-slate-500">
                    Ningún request capturado coincide con el filtro.
                  </p>
                ) : (
                  <>
                    <RequestList
                      requests={filtered}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      now={now}
                    />
                    {cursor && (
                      <div className="p-3">
                        <button
                          type="button"
                          onClick={() => void onLoadMore()}
                          className="w-full rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                        >
                          Cargar más antiguos
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          {(wide || selected) && (
            <section className="flex min-h-0 flex-col">
              {!wide && selected && (
                <div className="shrink-0 border-b border-slate-800 p-3">
                  <button
                    type="button"
                    onClick={() => setSelectedId(undefined)}
                    className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                  >
                    ← Volver a la lista
                  </button>
                </div>
              )}

              {selected ? (
                <RequestDetail request={selected} endpointUrl={captureUrl} />
              ) : (
                <p className="p-6 text-sm text-slate-500">
                  {requests.length === 0
                    ? 'Cuando llegue el primer request aparecerá aquí.'
                    : 'Selecciona un request de la lista. Puedes moverte con las flechas del teclado.'}
                </p>
              )}
            </section>
          )}
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="shrink-0 border-t border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-200"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function ExpiryNotice({
  expiresAt,
  now,
  renewing,
  onRenew,
}: {
  expiresAt: string;
  now: number;
  renewing: boolean;
  onRenew: () => void;
}) {
  if (hasExpired(expiresAt, now)) {
    return (
      <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-rose-300">
        <span>
          Este endpoint ha caducado: la próxima limpieza lo borrará junto con lo capturado.
        </span>
        <RenewButton renewing={renewing} onRenew={onRenew} />
      </p>
    );
  }

  const soon = expiresSoon(expiresAt, now);

  return (
    <p
      className={`mt-2 flex flex-wrap items-center gap-2 text-xs ${soon ? 'text-amber-300' : 'text-slate-500'}`}
    >
      <span>
        Caduca {timeUntil(expiresAt, now)}
        {soon && ' — cuando caduque se borrará todo lo capturado.'}
      </span>
      {soon && <RenewButton renewing={renewing} onRenew={onRenew} />}
    </p>
  );
}

/** The one-click rescue: only shown in the 24 h window (or after expiry). */
function RenewButton({ renewing, onRenew }: { renewing: boolean; onRenew: () => void }) {
  return (
    <button
      type="button"
      onClick={onRenew}
      disabled={renewing}
      className="rounded-md border border-sky-500/50 bg-sky-500/10 px-2 py-0.5 font-semibold text-sky-300 transition hover:bg-sky-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 disabled:opacity-50"
    >
      {renewing ? 'Renovando…' : 'Renovar 7 días'}
    </button>
  );
}

function EmptyState({ captureUrl }: { captureUrl: string }) {
  const snippet = exampleCurl(captureUrl);

  return (
    <div className="space-y-3 p-4">
      <p className="text-sm text-slate-400">
        Todavía no ha llegado nada. Pega esto en una terminal y lo verás aparecer aquí sin recargar:
      </p>
      <pre className="overflow-x-auto rounded-md bg-slate-950/70 p-3 font-mono text-xs leading-relaxed text-slate-200">
        {snippet}
      </pre>
      <CopyButton value={snippet} label="Copiar comando" />
    </div>
  );
}

/** For an endpoint created somewhere else: the token is all that is missing. */
function TokenPrompt({
  slug,
  error,
  onToken,
}: {
  slug: string;
  error?: string | undefined;
  onToken: (token: string) => void;
}) {
  const [value, setValue] = useState('');

  return (
    <main className="mx-auto max-w-lg px-6 py-20">
      <h1 className="text-2xl font-semibold text-slate-100">Hace falta el token</h1>
      <p className="mt-3 text-sm text-slate-400">
        Este navegador no tiene guardado el token de <code className="font-mono">/e/{slug}</code>.
        Pégalo para abrir el inspector; se guardará aquí para la próxima vez.
      </p>

      {error && (
        <p role="alert" className="mt-4 text-sm text-rose-300">
          {error}
        </p>
      )}

      <form
        className="mt-6 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const token = value.trim();
          if (!token) return;
          saveToken(slug, token, `${window.location.origin}/e/${slug}`);
          onToken(token);
        }}
      >
        <label className="sr-only" htmlFor="token">
          Token
        </label>
        <input
          id="token"
          type="password"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="Token de gestión"
          className="min-w-0 flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        />
        <button
          type="submit"
          className="rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
        >
          Abrir
        </button>
      </form>

      <button
        type="button"
        onClick={() => navigate('/')}
        className="mt-6 text-sm text-slate-400 underline hover:text-slate-200"
      >
        Volver al inicio
      </button>
    </main>
  );
}
